import React from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import TopFields, { FieldsConfig } from "@/components/layouts/TopFields";
import ContractStatusBadge from "@/pages/contracts/ContractsTable/ContractStatusBadge";
import UserAvatar from "@/components/controls/UserAvatar";
import ArrMrrEditableField from "@/pages/deals/components/commercials/ArrMrrEditableField";
import { useUsers } from "@/entities/users/hooks";
import { useUserSelect } from "@/entities/users/useUserSelect";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { useEntitySelect } from "@/services/hooks/entities/useEntitySelect";
import { useProducts } from "@/entities/settings/hooks";
import { ContractData, ContractRenewalType } from "@/pages/contracts/types";
import { getCommittedRevenue } from "@/pages/contracts/committedRevenue";
import { ReservedUserIds } from "@/entities/users/types";
import {
  useAccountCurrency,
  useCurrencyConversion
} from "@/services/hooks/useCurrencyConversion";
import { getCurrencySymbol } from "@/pages/deals/helpers";
import { reducePrecision } from "@/utils/numbers";
import { DEFAULT_CURRENCY_CODE } from "@/utils/numberFormat";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import {
  FINANCIALS_WRITE_PERMISSION,
  useHasPermission
} from "@/services/auth/AuthContext";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { ContractFieldUpdate } from "./ContractDetailedView";
import {
  ContractCloseFxRateField,
  ContractCurrentFxRateField
} from "./ContractFxRateFields";

interface Props {
  contract?: ContractData;
  onUpdateField: ContractFieldUpdate;
  className?: string;
  itemClassName?: string;
}

const ContractTopFields: React.FC<Props> = ({
  contract,
  onUpdateField,
  className,
  itemClassName
}) => {
  const { t } = useTranslation();
  const { isArr } = useArrMrr();
  const canEditFinancials = useHasPermission(FINANCIALS_WRITE_PERMISSION);
  const { enabled: showFxRates } = useFeatureFlag(
    FeatureFlags.CRM_11075_CONTRACT_FX_RATES
  );

  const { users: csmUsers, isLoading: isLoadingCsmUser } = useUsers(
    contract?.csmOwner ? [contract.csmOwner] : []
  );
  const csmUser = contract?.csmOwner
    ? csmUsers.find((u) => u.id === contract.csmOwner)
    : undefined;
  const csmIsKnown =
    csmUser && csmUser.id !== ReservedUserIds.UNKNOWN_USER_ID;

  const {
    options: userOptions,
    entities: userEntities,
    isLoading: isLoadingUsers,
    searchTerm: userSearchTerm,
    setSearchTerm: setUserSearchTerm
  } = useUserSelect();

  const {
    options: companyOptions,
    onSearch: onCompanyOptionsSearch,
    isLoading: isLoadingCompanies,
    searchTerm: companySearchTerm
  } = useEntitySelect({
    entityType: EntityType.COMPANY,
    selectedId: contract?.companyId || "",
    alwaysEnabled: true
  });

  const { data: productsData } = useProducts();
  const product = productsData?.products?.find(
    (p) => p.id === contract?.productId
  );

  const rawCurrency = contract?.currency ?? undefined;
  const currency = rawCurrency || DEFAULT_CURRENCY_CODE;
  const contractCurrencySymbol = getCurrencySymbol(currency);
  // Pass the unresolved currency (not the display fallback) so the hook's
  // own "unknown currency" guard can suppress fabricating a conversion.
  const { showConversion, accountCurrencySymbol, rate } =
    useCurrencyConversion(rawCurrency);
  // Read separately: useCurrencyConversion substitutes USD while the setting
  // loads so the rate lookup has a base, and comparing a contract against that
  // guess qualifies a sign that needs no qualifying until the real currency
  // arrives.
  const accountCurrency = useAccountCurrency();

  if (!contract) return null;

  const tableStatus = contract.status;
  const isAutoRenewal = contract.renewalType === ContractRenewalType.AUTO;
  // Same precedence as the Financial Recognition panel beside it: the override
  // when set, otherwise the service-calculated figure, otherwise the field's
  // empty state rather than a fabricated 0.
  const { effective: committed } = getCommittedRevenue(contract, isArr);
  const committedLocalValue =
    committed.local != null ? reducePrecision(committed.local, 0) : null;
  const committedAccountValue =
    committed.account != null ? reducePrecision(committed.account, 0) : null;

  // A rate needs both currencies named, and is meaningless for a contract
  // already in the account's currency — which is when showConversion is off.
  const fxCurrencies =
    showFxRates && showConversion && rawCurrency && accountCurrency
      ? { contractCurrency: rawCurrency, accountCurrency }
      : null;

  const fieldsConfig: FieldsConfig = {
    companyId: {
      type: "select",
      icon: "company",
      entity: EntityType.COMPANY,
      options: companyOptions,
      searchTerm: companySearchTerm,
      showSearch: true,
      onSearch: onCompanyOptionsSearch,
      isLoading: isLoadingCompanies,
      popoverClassName: "max-w-[30vw]",
      onSelect: (companyId: string) => onUpdateField("companyId", companyId)
    },
    productId: {
      type: "nonEditable",
      icon: "product",
      customComponent: (
        <Text
          variant="body"
          className="px-2"
          data-test="contract-top-fields-product"
        >
          {product?.name ?? "—"}
        </Text>
      )
    },
    startDate: {
      type: "date",
      icon: "calendar",
      placeholder: t("contracts.detailedView.selectStartDate")
    },
    endDate: isAutoRenewal
      ? {
          type: "nonEditable",
          customComponent: (
            <Text
              variant="body"
              className="px-2"
              data-test="contract-top-fields-end-date-ongoing"
            >
              {t("contracts.detailedView.ongoing")}
            </Text>
          )
        }
      : {
          type: "date"
        },
    csmOwner: {
      type: "select",
      entity: EntityType.USER,
      options: userOptions,
      showEmptyIcon: true,
      showSearch: true,
      searchTerm: userSearchTerm,
      onSearch: setUserSearchTerm,
      isLoading: isLoadingUsers,
      renderOptionIcon: (value) => {
        const user =
          userEntities.find((u) => u.id === (value as string)) ??
          (csmUser?.id === (value as string) ? csmUser : undefined);
        return <UserAvatar userData={user} size="S" className="mr-1" />;
      },
      additionalProps: {
        // No owner is a choice not yet made, not missing data — "Unknown" is
        // kept for an owner that is set but cannot be resolved.
        placeholder: !contract?.csmOwner
          ? t("unassigned")
          : csmIsKnown && csmUser
            ? csmUser.displayName
            : isLoadingCsmUser
              ? ""
              : t("unknown")
      }
    },
    totalContractValue: {
      type: "nonEditable",
      icon: "arrMrr",
      customComponent: (
        <div className="flex items-center gap-0.5 px-2">
          <Text variant="body">{t("contracts.tcv")}</Text>
          <ArrMrrEditableField
            fieldName="totalContractValueLocal"
            label={t("contracts.tcv")}
            value={reducePrecision(contract.totalContractValueLocal ?? 0, 0)}
            originalValue={reducePrecision(contract.totalContractValue ?? 0, 0)}
            contractCurrencySymbol={contractCurrencySymbol}
            accountCurrencySymbol={accountCurrencySymbol}
            contractCurrencyCode={rawCurrency}
            accountCurrencyCode={accountCurrency}
            showConversion={showConversion}
            rate={rate}
            onSave={(field, value) => {
              if (!canEditFinancials) return;
              onUpdateField(field, value === "" ? null : (value as number));
            }}
            entityId={contract.id}
            disabled={!canEditFinancials}
            disabledTooltipText={t("common.noEditPermission")}
          />
        </div>
      )
    },
    cmrr: {
      type: "nonEditable",
      icon: "arrMrr",
      customComponent: (
        <div className="flex items-center gap-0.5 px-2">
          <Text variant="body">
            {isArr ? t("contracts.carr") : t("contracts.cmrr")}
          </Text>
          <ArrMrrEditableField
            fieldName={isArr ? "carrLocal" : "cmrrLocal"}
            label={isArr ? t("contracts.carr") : t("contracts.cmrr")}
            value={committedLocalValue}
            originalValue={committedAccountValue}
            contractCurrencySymbol={contractCurrencySymbol}
            accountCurrencySymbol={accountCurrencySymbol}
            contractCurrencyCode={rawCurrency}
            accountCurrencyCode={accountCurrency}
            showConversion={showConversion}
            rate={rate}
            onSave={(field, value) => {
              if (!canEditFinancials) return;
              onUpdateField(field, value === "" ? null : (value as number));
            }}
            entityId={contract.id}
            disabled={!canEditFinancials}
            disabledTooltipText={t("common.noEditPermission")}
          />
        </div>
      )
    },
    ...(fxCurrencies
      ? {
          closeFxRate: {
            type: "nonEditable" as const,
            customComponent: (
              <ContractCloseFxRateField
                rate={contract.closeFxRate}
                {...fxCurrencies}
              />
            )
          },
          currentFxRate: {
            type: "nonEditable" as const,
            customComponent: (
              <ContractCurrentFxRateField
                contract={contract}
                {...fxCurrencies}
                canEdit={canEditFinancials}
                onUpdateField={onUpdateField}
              />
            )
          }
        }
      : {}),
    contractStatus: {
      type: "nonEditable",
      customComponent:
        tableStatus != null ? (
          <ContractStatusBadge
            status={tableStatus}
            className="border-none text-base"
          />
        ) : undefined
    }
  };

  return (
    <TopFields
      entity={contract}
      fieldsConfig={fieldsConfig}
      onUpdateField={onUpdateField}
      className={className}
      itemClassName={itemClassName}
    />
  );
};

export default ContractTopFields;
